import { Link } from 'react-router-dom';

import { GridInitialStatePremium } from '@mui/x-data-grid-premium/models/gridStatePremium';
import { uniqueId } from 'lodash';
import noop from 'lodash/noop';

import {
  Box,
  Button,
  ButtonGroup,
  GridColDef,
  GridRenderCellParams,
  StateAwareGridPremium,
  textFilterConfig,
  useConfirmationModal,
  selectFilterConfig as selectFilterOptions,
} from '@sbv-clients/ui-shared';
import { nameOfFactory } from '@sbv-clients/util-misc';

import {
  FacilityType,
  useAdminFacilitiesGet,
  useSyncFacility,
} from '../../../services/admin';
import { useFiltersModel } from './use-filters-model';
import { usePaginationModel } from './use-pagination-model';

const initialState: GridInitialStatePremium = {
  pagination: { paginationModel: { pageSize: 25 } },
};

const facilityRowKey = nameOfFactory<FacilityType>();

const selectFilterConfig = () => {
  return {
    ...selectFilterOptions(),
  };
};

const viewOnlyColumnConfig = () => {
  return {
    sortable: false,
    filterable: false,
    aggregable: false,
    groupable: false,
    pinnable: false,
  };
};

type FacilityActionsProps = {
  row: FacilityType;
};

const FacilityActions = ({ row }: FacilityActionsProps) => {
  const { mutateAsync: syncFacility, isLoading: isSyncing } = useSyncFacility();
  const { modal: confirmModal, openModal: openConfirmModal } =
    useConfirmationModal();

  const id = row.id as string;
  const teamSupportId = row.teamSupportId as string;
  const facilityName = row.facilityName as string;
  const onboarded = row.onboarded as boolean;
  const disabled = !id;
  const syncDisabled = disabled || !onboarded || isSyncing;

  return (
    <>
      {confirmModal}
      <Box display="flex" height="100%" alignItems="center" gap={1}>
        <ButtonGroup variant="contained" size="small">
          {id ? (
            <Button
              component={Link}
              to={`/facility/${id}/edit`}
              sx={{ width: 90 }}
            >
              Edit
            </Button>
          ) : (
            <Button
              component={Link}
              to={`/facility/${facilityName}/onboard?teamSupportId=${teamSupportId}`}
              sx={{ width: 90 }}
            >
              Onboard
            </Button>
          )}
          <Button
            component={Link}
            to={`/facility/${id}/configuration`}
            disabled={disabled}
          >
            Configuration
          </Button>
          <Button
            component={Link}
            to={`/facility/${id}/users`}
            disabled={disabled}
          >
            Users
          </Button>
          <Button
            component={Link}
            to={`/facility/${id}/menu-permissions`}
            disabled={disabled}
          >
            Permissions
          </Button>
          <Button
            component={Link}
            to={`/facilities-stats?facilityId=${id}`}
            disabled={disabled}
          >
            Stats
          </Button>
          <Button
            disabled={syncDisabled}
            onClick={() =>
              openConfirmModal({
                title: 'Sync employees',
                content:
                  'Please confirm you would like to sync all employees in this facility. This can take up to 2 minutes for large facilities.',
                confirmLabel: 'Sync employees',
              })
                .then(() => syncFacility(id))
                .catch(noop)
            }
          >
            Sync employees
          </Button>
        </ButtonGroup>
      </Box>
    </>
  );
};

const FacilityGrid = () => {
  const { paginationModel, setPaginationModel } = usePaginationModel();
  const { filterModel, setFilterModel, apiFilters } = useFiltersModel();
  const { isLoading, isRefetching, data } = useAdminFacilitiesGet(
    {
      ...paginationModel,
      page: paginationModel.page + 1,
      ...apiFilters,
    },
    { keepPreviousData: true }
  );
  const facilities = data?.facilities ?? [];
  const rowCount = data?.totalCount ?? 0;

  const columns: GridColDef[] = [
    {
      type: 'string',
      field: facilityRowKey('facilityName'),
      headerName: 'Database name',
      ...textFilterConfig(),
    },
    {
      field: facilityRowKey('onboarded'),
      headerName: 'Added to Saas',
      headerAlign: 'center',
      align: 'center',
      ...selectFilterConfig(),
    },
    {
      field: facilityRowKey('usingMobile'),
      headerName: 'Using Mobile',
      headerAlign: 'center',
      align: 'center',
      ...selectFilterConfig(),
    },
    {
      field: facilityRowKey('usingSaaSAuthentication'),
      headerName: 'Using SaaS \n Authentication',
      headerClassName: 'multi-line-header',
      headerAlign: 'center',
      align: 'center',
      ...selectFilterConfig(),
    },
    {
      field: facilityRowKey('usingSbvPro'),
      headerName: 'Using SBV Pro',
      headerAlign: 'center',
      align: 'center',
      ...selectFilterConfig(),
    },
    {
      type: 'number',
      field: facilityRowKey('registeredMobileCount'),
      headerName: '# Mobile \n Registered',
      headerClassName: 'multi-line-header',
      headerAlign: 'center',
      align: 'center',
      ...viewOnlyColumnConfig(),
    },
    {
      type: 'number',
      field: facilityRowKey('registeredDesktopCount'),
      headerName: '# Desktop \n Registered',
      headerClassName: 'multi-line-header',
      headerAlign: 'center',
      align: 'center',
      ...viewOnlyColumnConfig(),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      minWidth: 700,
      flex: 0,
      ...viewOnlyColumnConfig(),
      renderCell: (params: GridRenderCellParams<FacilityType>) => (
        <FacilityActions row={params.row} />
      ),
    },
  ];
  const getRowId = () => uniqueId();
  return (
    <StateAwareGridPremium
      name="facility-grid"
      applyDefaultBounds={false}
      pagination
      initialState={initialState}
      pageSizeOptions={[10, 25, 50, 75]}
      paginationMode="server"
      rows={facilities}
      loading={isLoading || isRefetching}
      rowCount={rowCount}
      columns={columns}
      disableRowSelectionOnClick
      getRowId={getRowId}
      filterMode="server"
      filterModel={filterModel}
      sx={{
        '& .multi-line-header .MuiDataGrid-columnHeaderTitle': {
          whiteSpace: 'pre-line',
        },
      }}
      onFilterModelChange={setFilterModel}
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
    />
  );
};

export default FacilityGrid;
